import { HeadCell } from '../../components/formlib/TableHeader';
import { EmployeeType } from '../../utils/type';
import {
  HourlyNonExemptEmployeeHeader,
  SalariedNonExemptEmployeeHeader,
  SalariedEmployeeHeader,
} from './ActivityListTableView';
import { Productivity } from '../../services/configApi/activitySheet/weeklyActivitySheetProvider';
import { ReviewEmployeeHeader } from './types';
import { Label } from '../../components/charts/DoughnutChart';
import dayjs from 'dayjs';

export const FilterStatuses: string[] = [
  'notSubmitted',
  'submitted',
  'approved',
  'rejected',
];

export const workingLabelIds: Label[] = [
  { labelId: 'billableHrsText', iconColor: '#5573FF', visible: true },
  { labelId: 'anticipatedHoursText', iconColor: '#22DFA6', visible: true },
];

export const nonWorkingLabelIds: Label[] = [
  { labelId: 'etHoursText', iconColor: '#22DFA6', visible: true },
  { labelId: 'ltiHoursText', iconColor: '#FFCA42', visible: true },
  { labelId: 'holidayTimeText', iconColor: '#F96300', visible: true },
  { labelId: 'vacationHoursText', iconColor: '#37D183', visible: true },
  { labelId: 'profHoursText', iconColor: '#5573FF', visible: true },
  { labelId: 'otherHoursText', iconColor: '#FF8354', visible: true },
  { labelId: '', iconColor: '#D9D9D9', visible: false },
];

export const getReviewActivitySheetHeaders = () => {
  const reviewActivityEmployeesHeaderData: HeadCell<ReviewEmployeeHeader>[] = [
    {
      id: 'lastName',
      labelId: 'employeeNameText',
      defaultLabelId: 'Employee Name',
      numeric: false,
      requiredSorting: true,
    },
    {
      id: 'employeeType',
      labelId: 'employeetypeText',
      defaultLabelId: 'Employee Type',
      numeric: false,
      requiredSorting: false,
    },
    {
      id: 'billableHoursDec',
      labelId: 'billableHrsText',
      defaultLabelId: 'Billable Hrs',
      numeric: false,
      requiredSorting: false,
    },
    {
      id: 'otherAssocWorkHoursDec',
      labelId: 'otherAssocWorkHrsText',
      defaultLabelId: 'Other Assoc. Work Hrs',
      numeric: false,
      requiredSorting: false,
    },
    {
      id: 'approvedNonBillableHoursDec',
      labelId: 'approvedNonBillableHrsText',
      defaultLabelId: 'Apvd Non-Bill. Hrs',
      numeric: false,
      requiredSorting: false,
    },
    {
      id: 'hoursOutDec',
      labelId: 'hoursOutText',
      defaultLabelId: 'Hours Out',
      numeric: false,
      requiredSorting: false,
    },
    {
      id: 'totalHoursDec',
      labelId: 'totalHrsText',
      defaultLabelId: 'Total Hours',
      numeric: false,
      requiredSorting: false,
    },
    {
      id: 'productivity',
      labelId: 'productivityText',
      defaultLabelId: 'Productivity',
      numeric: false,
      requiredSorting: false,
    },
    {
      id: 'payrollStatus',
      labelId: 'payrollStatusText',
      defaultLabelId: 'Payroll Status',
      numeric: false,
      requiredSorting: false,
    },
    {
      id: 'billingStatus',
      labelId: 'billingStatusText',
      defaultLabelId: 'Billing Status',
      numeric: false,
      requiredSorting: false,
    },
    {
      id: 'actions',
      labelId: 'action',
      defaultLabelId: 'Action',
      numeric: false,
      requiredSorting: false,
    },
  ];
  return reviewActivityEmployeesHeaderData;
};

export const getHeaderValues = (employeeType: EmployeeType) => {
  let headerValues:
    | HeadCell<HourlyNonExemptEmployeeHeader>[]
    | HeadCell<SalariedNonExemptEmployeeHeader>[]
    | HeadCell<SalariedEmployeeHeader>[] = [];
  switch (employeeType) {
    case 'Hourly Non-Exempt':
      {
        const hourlyNonExemptHeaderData: HeadCell<HourlyNonExemptEmployeeHeader>[] =
          [
            {
              id: 'day',
              labelId: 'dayText',
              defaultLabelId: 'Day',
              numeric: false,
              requiredSorting: false,
            },
            {
              id: 'billableHoursDec',
              labelId: 'billableHrsText',
              defaultLabelId: 'Billable Hrs',
              numeric: false,
              requiredSorting: false,
            },
            {
              id: 'otherAssocWorkHrsDec',
              labelId: 'otherAssocWorkHrsText',
              defaultLabelId: 'Other Assoc. Work Hrs',
              numeric: false,
              requiredSorting: false,
            },
            {
              id: 'approvedNonBillingHrs',
              labelId: 'approvedNonBillableHrsText',
              defaultLabelId: 'Apvd Non-Bill. Hrs',
              numeric: false,
              requiredSorting: false,
            },
            {
              id: 'noShowTravelWaitDoc',
              labelId: 'noShowText',
              defaultLabelId: 'No Show',
              numeric: false,
              requiredSorting: false,
              requireInfoIcon: true,
              tooltipProps: {
                labelId: 'noShowDescText',
                defaultLabel: 'No Show (Travel, Wait, Doc, Other)',
              },
            },
            {
              id: 'otherNonBillableHrsDec',
              labelId: 'otherNonBillableHrsText',
              defaultLabelId: 'Other Non-Billable Hrs',
              numeric: false,
              requiredSorting: false,
            },
            {
              id: 'hrsOut',
              labelId: 'hoursOutText',
              defaultLabelId: 'Hours Out',
              numeric: false,
              requiredSorting: false,
            },
            {
              id: 'lunchHrs',
              labelId: 'lunchText',
              defaultLabelId: 'Lunch',
              numeric: false,
              requiredSorting: false,
            },
            {
              id: 'totalHrs',
              labelId: 'totalHrsText',
              defaultLabelId: 'Total Hrs',
              numeric: false,
              requiredSorting: false,
            },
          ];
        headerValues = hourlyNonExemptHeaderData;
      }
      return headerValues;
    case 'Salaried Non-Exempt':
      {
        const salariedNonExemptHeaderData: HeadCell<SalariedNonExemptEmployeeHeader>[] =
          [
            {
              id: 'day',
              labelId: 'dayText',
              defaultLabelId: 'Day',
              numeric: false,
              requiredSorting: false,
            },
            {
              id: 'billableHoursDec',
              labelId: 'billableHrsText',
              defaultLabelId: 'Billable Hrs',
              numeric: false,
              requiredSorting: false,
            },
            {
              id: 'otherAssocWorkHrsDec',
              labelId: 'otherAssocWorkHrsText',
              defaultLabelId: 'Other Assoc. Work Hrs',
              numeric: false,
              requiredSorting: false,
            },
            {
              id: 'approvedNonBillingHrs',
              labelId: 'approvedNonBillableHrsText',
              defaultLabelId: 'Apvd Non-Bill. Hrs',
              numeric: false,
              requiredSorting: false,
            },
            {
              id: 'otherNonBillableHrsDec',
              labelId: 'otherNonBillableHrsText',
              defaultLabelId: 'Other Non-Billable Hrs',
              numeric: false,
              requiredSorting: false,
            },
            {
              id: 'hrsOut',
              labelId: 'hoursOutText',
              defaultLabelId: 'Hours Out',
              numeric: false,
              requiredSorting: false,
            },
            {
              id: 'lunchHrs',
              labelId: 'lunchText',
              defaultLabelId: 'Lunch',
              numeric: false,
              requiredSorting: false,
            },
            {
              id: 'totalHrs',
              labelId: 'totalHrsText',
              defaultLabelId: 'Total Hrs',
              numeric: false,
              requiredSorting: false,
            },
          ];
        headerValues = salariedNonExemptHeaderData;
      }
      return headerValues;
    case 'Salaried Exempt':
      {
        const salariedHeaderData: HeadCell<SalariedEmployeeHeader>[] = [
          {
            id: 'day',
            labelId: 'dayText',
            defaultLabelId: 'Day',
            numeric: false,
            requiredSorting: false,
          },
          {
            id: 'billableHoursDec',
            labelId: 'billableHrsText',
            defaultLabelId: 'Billable Hrs',
            numeric: false,
            requiredSorting: false,
          },
          {
            id: 'approvedNonBillingHrs',
            labelId: 'apvdNonBillableHrsText',
            defaultLabelId: 'Apvd Non-Billable Hrs',
            numeric: false,
            requiredSorting: false,
          },
          {
            id: 'otherNonBillableHrsDec',
            labelId: 'otherNonBillableHrsText',
            defaultLabelId: 'Other Non-Billable Hrs',
            numeric: false,
            requiredSorting: false,
          },
          {
            id: 'hrsOut',
            labelId: 'hoursOutText',
            defaultLabelId: 'Hours Out',
            numeric: false,
            requiredSorting: false,
          },
          {
            id: 'totalHrs',
            labelId: 'totalHrsText',
            defaultLabelId: 'Total Hrs',
            numeric: false,
            requiredSorting: false,
          },
        ];
        headerValues = salariedHeaderData;
      }
      return headerValues;

    default:
      return headerValues;
  }
};

export const productivityData = (productivity: Productivity[] | null) => {
  const data: string[] = [];
  if (productivity !== null && productivity.length > 0) {
    for (const week of productivity) {
      data.push(week.percent);
    }
  }
  return data;
};
export const productivityLabels = (productivity: Productivity[] | null) => {
  if (productivity !== null && productivity.length > 0) {
    const output = productivity.map((week) => {
      const start = dayjs(week.weekStart, 'MM/DD/YYYY').format('MM/DD');
      const end = dayjs(week.weekEnd, 'MM/DD/YYYY').format('MM/DD');
      return `${start} - ${end}`;
    });
    return output;
  }
  return [];
};

export const getBackgroundColors = (productivity: Productivity[] | null) => {
  const colors: string[] = ['#008C82', '#008C82', '#008C82', '#008C82'];
  if (productivity !== null && productivity.length > 0) {
    const lastProductivity = productivity.at(-1);
    if (!lastProductivity) {
      return colors;
    }
    if (
      dayjs().isAfter(dayjs(lastProductivity.weekStart, 'MM/DD/YYYY')) &&
      dayjs().isBefore(dayjs(lastProductivity.weekEnd, 'MM/DD/YYYY'))
    ) {
      colors.push('#BB6BD9');
    } else if (dayjs().format('MM/DD/YYYY') === lastProductivity.weekEnd) {
      colors.push('#BB6BD9');
    } else {
      colors.push('#008C82');
    }
  }
  return colors;
};
